import { AppContext } from "@Context/common/general/context";
import { getBybIqShade } from "@Helpers/feedback";
import { Box, useTheme } from "@mui/material";
import { StandardPalette } from "@Themes/palettes/mui-palette-extension";
import { useContext, useEffect, useRef, useState } from "react";

const viewBoxSize = 24;
const padding = 2;

interface IFillingBrainIconProps {
  size?: number;
  fillPercentage?: number;
  showShadow?: boolean;
  target?: number;
  loading?: boolean;
  loadingVariant?: "wave" | "fill";
  loadingFillColor?: string;
  loadingDuration?: string;
}

const FillingBrainIcon = ({
  size = 200,
  fillPercentage = 0,
  showShadow = true,
  target,
  loading = false,
  loadingVariant = "wave",
  loadingFillColor,
  loadingDuration,
}: IFillingBrainIconProps) => {
  const theme = useTheme();
  const { state } = useContext(AppContext);
  const [isMounted, setIsMounted] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const rectRef = useRef<SVGRectElement>(null);
  const isVR = state.device.isVR && state.device.isUnityEmbedded;

  const height = fillPercentage;
  const targetY = padding + (viewBoxSize - 2 * padding) * (1 - height / 100);
  const targetHeight = viewBoxSize - padding * 2;
  const duration = Math.max(fillPercentage * 15, 1000);

  const color = getBybIqShade(fillPercentage * 10, theme, state.user.creationDate);
  const loadingAnimationDuration = loadingDuration || (loadingVariant === "fill" ? "2.8s" : "1.5s");
  const loadingColor = loadingFillColor || theme.palette.dsSurface.v200;

  const targetYPercent = target !== undefined ? 1 - target / 100 : 0;
  const targetSize = size * 0.03;
  const targetSpacing = size * 0.01;

  useEffect(() => {
    setIsMounted(false);
    const timer = setTimeout(() => {
      setIsMounted(true);
      setAnimationKey(prev => prev + 1);
    }, 50);
    return () => clearTimeout(timer);
  }, [fillPercentage]);

  useEffect(() => {
    if (isMounted && rectRef.current) {
      const rect = rectRef.current;
      const animateElements = rect.querySelectorAll("animate");
      animateElements.forEach(animate => {
        animate.beginElement();
      });
    }
  }, [isMounted, animationKey]);

  return (
    <Box position="relative" width={size} height={size} display="inline-block">
      <svg height="100%" width="100%" viewBox={`-0.5 0 ${viewBoxSize + 1} ${viewBoxSize}`} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="brain-mask">
            <g fill="white">
              <path
                d="M14.4981 1.99609C14.2066 2.00054 13.917 2.04221 13.6361 2.12009C13.5461 2.14809 13.4591 2.17709 13.3721 2.21009C13.2337 2.26248 13.1121 2.35128 13.0201 2.46709C12.9002 2.61922 12.8335 2.80644 12.8301 3.00009V20.9891C12.8294 21.1546 12.8782 21.3165 12.9701 21.4541C13.1331 21.6773 13.3773 21.8274 13.6501 21.8721C13.9403 21.9512 14.2393 21.9932 14.5401 21.9971H14.6631C15.1092 21.9957 15.55 21.9006 15.9569 21.7178C16.3639 21.535 16.7278 21.2687 17.0251 20.9361C17.6639 20.2919 18.0651 19.45 18.1631 18.5481C19.2108 18.4769 20.1908 18.0045 20.8991 17.2291C21.5917 16.4738 21.9831 15.4907 21.9991 14.4661V14.3411C21.9991 14.3171 21.9991 14.2951 21.9991 14.2721C21.9959 13.8683 21.9387 13.4667 21.8291 13.0781C21.7192 12.6712 21.5608 12.279 21.3571 11.9101C21.5613 11.5764 21.72 11.2169 21.8291 10.8411C21.9432 10.4486 22.0005 10.0418 21.9991 9.63309C22.0074 9.09628 21.9083 8.56322 21.7078 8.06522C21.5072 7.56721 21.2091 7.11431 20.8311 6.73309C20.1054 5.9545 19.115 5.47516 18.0541 5.38909C18.0147 4.47755 17.6262 3.61609 16.9691 2.98309C16.3384 2.36322 15.4933 2.0098 14.6091 1.99609L14.4981 1.99609Z"
                transform="translate(0.25, 0)"
              />
              <path
                d="M9.38528 1.99609C8.50138 2.00995 7.65669 2.36337 7.02628 2.98309C6.37118 3.61503 5.98293 4.47383 5.94128 5.38309C4.88007 5.46975 3.88942 5.94938 3.16328 6.72809C2.78467 7.10976 2.4862 7.5633 2.28544 8.06201C2.08469 8.56073 1.98569 9.09456 1.99428 9.63209C1.99348 10.0412 2.05106 10.4483 2.16528 10.8411C2.27505 11.2166 2.43375 11.576 2.63728 11.9101C2.43431 12.2794 2.27587 12.6715 2.16528 13.0781C2.05495 13.4663 1.99743 13.8676 1.99428 14.2711C1.9597 15.3631 2.35463 16.425 3.09428 17.2291C3.80267 18.0043 4.78257 18.4767 5.83028 18.5481C5.92975 19.45 6.33155 20.2916 6.97028 20.9361C7.26755 21.2686 7.6315 21.5349 8.03845 21.7175C8.4454 21.9001 8.88623 21.9951 9.33228 21.9961H9.45428C9.75504 21.9922 10.0541 21.9502 10.3443 21.8711C10.6169 21.8262 10.8611 21.6761 11.0243 21.4531C11.1155 21.3152 11.1638 21.1534 11.1633 20.9881V3.00209C11.161 2.8084 11.0945 2.62094 10.9743 2.46909C10.8827 2.35276 10.761 2.26386 10.6223 2.21209C10.5363 2.17909 10.4473 2.14909 10.3583 2.12109C10.0768 2.04275 9.78643 2.00075 9.49428 1.99609H9.38528Z"
                transform="translate(-0.25, 0)"
              />
            </g>
          </mask>
          <filter id="shadow">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.3" />
          </filter>
          {/*  */}
          <linearGradient id="fillGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color={isVR ? color.v300 : (color as StandardPalette).v400} />
            <stop offset={`${fillPercentage / 5}%`} stop-color={isVR ? color.v200 : color.v300} />
            <stop offset="100%" stop-color={isVR ? color.v100 : color.v200} />
          </linearGradient>
          {loading && loadingVariant === "wave" && (
            <linearGradient id="skeletonWaveGradient" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
              <stop offset="0%" stop-color={theme.palette.dsSurface.v400} />
              <stop offset="40%" stop-color={theme.palette.dsSurface.v400}>
                <animate attributeName="offset" values="0;1;0" dur={loadingAnimationDuration} repeatCount="indefinite" />
              </stop>
              <stop offset="40%" stop-color={theme.palette.dsSurface.v200}>
                <animate attributeName="offset" values="0;1;0" dur={loadingAnimationDuration} repeatCount="indefinite" />
              </stop>
              <stop offset="60%" stop-color={theme.palette.dsSurface.v200}>
                <animate attributeName="offset" values="0;1;0" dur={loadingAnimationDuration} repeatCount="indefinite" />
              </stop>
              <stop offset="60%" stop-color={theme.palette.dsSurface.v400}>
                <animate attributeName="offset" values="0;1;0" dur={loadingAnimationDuration} repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stop-color={theme.palette.dsSurface.v400} />
            </linearGradient>
          )}
          {loading && loadingVariant === "fill" && (
            <linearGradient id="loadingFillGradient" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
              <stop offset="0%" stop-color={loadingColor} />
              <stop offset="100%" stop-color={loadingColor} />
            </linearGradient>
          )}
        </defs>

        <g fill={theme.palette.dsBase.v200} opacity={0.08} stroke={theme.palette.dsBase.v200} strokeWidth="1">
          <path
            d="M14.4981 1.99609C14.2066 2.00054 13.917 2.04221 13.6361 2.12009C13.5461 2.14809 13.4591 2.17709 13.3721 2.21009C13.2337 2.26248 13.1121 2.35128 13.0201 2.46709C12.9002 2.61922 12.8335 2.80644 12.8301 3.00009V20.9891C12.8294 21.1546 12.8782 21.3165 12.9701 21.4541C13.1331 21.6773 13.3773 21.8274 13.6501 21.8721C13.9403 21.9512 14.2393 21.9932 14.5401 21.9971H14.6631C15.1092 21.9957 15.55 21.9006 15.9569 21.7178C16.3639 21.535 16.7278 21.2687 17.0251 20.9361C17.6639 20.2919 18.0651 19.45 18.1631 18.5481C19.2108 18.4769 20.1908 18.0045 20.8991 17.2291C21.5917 16.4738 21.9831 15.4907 21.9991 14.4661V14.3411C21.9991 14.3171 21.9991 14.2951 21.9991 14.2721C21.9959 13.8683 21.9387 13.4667 21.8291 13.0781C21.7192 12.6712 21.5608 12.279 21.3571 11.9101C21.5613 11.5764 21.72 11.2169 21.8291 10.8411C21.9432 10.4486 22.0005 10.0418 21.9991 9.63309C22.0074 9.09628 21.9083 8.56322 21.7078 8.06522C21.5072 7.56721 21.2091 7.11431 20.8311 6.73309C20.1054 5.9545 19.115 5.47516 18.0541 5.38909C18.0147 4.47755 17.6262 3.61609 16.9691 2.98309C16.3384 2.36322 15.4933 2.0098 14.6091 1.99609L14.4981 1.99609Z"
            transform="translate(0.25, 0)"
          />
          <path
            d="M9.38528 1.99609C8.50138 2.00995 7.65669 2.36337 7.02628 2.98309C6.37118 3.61503 5.98293 4.47383 5.94128 5.38309C4.88007 5.46975 3.88942 5.94938 3.16328 6.72809C2.78467 7.10976 2.4862 7.5633 2.28544 8.06201C2.08469 8.56073 1.98569 9.09456 1.99428 9.63209C1.99348 10.0412 2.05106 10.4483 2.16528 10.8411C2.27505 11.2166 2.43375 11.576 2.63728 11.9101C2.43431 12.2794 2.27587 12.6715 2.16528 13.0781C2.05495 13.4663 1.99743 13.8676 1.99428 14.2711C1.9597 15.3631 2.35463 16.425 3.09428 17.2291C3.80267 18.0043 4.78257 18.4767 5.83028 18.5481C5.92975 19.45 6.33155 20.2916 6.97028 20.9361C7.26755 21.2686 7.6315 21.5349 8.03845 21.7175C8.4454 21.9001 8.88623 21.9951 9.33228 21.9961H9.45428C9.75504 21.9922 10.0541 21.9502 10.3443 21.8711C10.6169 21.8262 10.8611 21.6761 11.0243 21.4531C11.1155 21.3152 11.1638 21.1534 11.1633 20.9881V3.00209C11.161 2.8084 11.0945 2.62094 10.9743 2.46909C10.8827 2.35276 10.761 2.26386 10.6223 2.21209C10.5363 2.17909 10.4473 2.14909 10.3583 2.12109C10.0768 2.04275 9.78643 2.00075 9.49428 1.99609H9.38528Z"
            transform="translate(-0.25, 0)"
          />
        </g>

        {loading && loadingVariant === "wave" && (
          <rect
            x={padding - 0.5}
            y={padding}
            width={viewBoxSize - padding * 2 + 1}
            height={viewBoxSize - padding * 2}
            fill="url(#skeletonWaveGradient)"
            mask="url(#brain-mask)"
          />
        )}

        {loading && loadingVariant === "fill" && (
          <rect
            x={padding - 0.5}
            y={viewBoxSize}
            width={viewBoxSize - padding * 2 + 1}
            height={0}
            fill="url(#loadingFillGradient)"
            mask="url(#brain-mask)"
          >
            <animate attributeName="height" values="0;20;0" dur={loadingAnimationDuration} repeatCount="indefinite" />
            <animate attributeName="y" values="22;2;22" dur={loadingAnimationDuration} repeatCount="indefinite" />
          </rect>
        )}

        {!loading && isMounted && (
          <rect
            key={animationKey}
            ref={rectRef}
            x={padding - 0.5}
            y={viewBoxSize}
            width={viewBoxSize - padding * 2 + 1}
            height={0}
            fill="url(#fillGradient)"
            mask="url(#brain-mask)"
          >
            <animate id={`height-animate-${animationKey}`} attributeName="height" from="0" to={targetHeight} dur={`${duration}ms`} fill="freeze" />
            <animate id={`y-animate-${animationKey}`} attributeName="y" from={viewBoxSize} to={targetY} dur={`${duration}ms`} fill="freeze" />
          </rect>
        )}
      </svg>

      {target !== undefined && (
        <svg
          width={targetSize * 2}
          height={targetSize * 2}
          style={{
            position: "absolute",
            right: -targetSize * 2 - targetSpacing,
            top: Math.max(0, Math.min(size - targetSize * 2, targetYPercent * size - targetSize)),
            pointerEvents: "none",
          }}
          viewBox="0 0 10 10"
        >
          <polygon
            points="4.5,5 9,1 9,9"
            fill={theme.palette.dsNeutral.v300}
            stroke={theme.palette.dsNeutral.v300}
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {showShadow && (
        <svg
          height={30}
          viewBox={`-0.5 0 ${viewBoxSize + 1} ${viewBoxSize}`}
          style={{
            position: "absolute",
            bottom: -8,
            left: "25%",
            width: "50%",
            opacity: 0.2,
          }}
        >
          <defs>
            <filter id="blur">
              <feGaussianBlur stdDeviation="2" />
            </filter>
          </defs>
          <ellipse
            cx={viewBoxSize / 2}
            cy={viewBoxSize - 5 * padding}
            rx={viewBoxSize * 1.5}
            ry={viewBoxSize / 4}
            fill={theme.palette.dsSurface.v200}
            filter="url(#blur)"
          />
        </svg>
      )}
    </Box>
  );
};

export default FillingBrainIcon;
